import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SimulatorForm from "./components/SimulatorForm";
import SimulatorResultView from "./components/SimulatorResult";
import { type SimulatorInput, getDefaultInput, calculateSimulator } from "./components/SimulatorEngine";

export default function InvestmentSimulatorPage() {
  const [data, setData] = useState<SimulatorInput>(getDefaultInput);
  const result = useMemo(() => calculateSimulator(data), [data]);

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-brain-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">도구</span>
              <span className="px-1.5 py-0.5 bg-[#C4956A]/10 text-[#C4956A] text-[10px] rounded font-semibold">NEW</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-1">
              경매 단기매매 AI 투자 판단 시뮬레이터
            </h1>
            <p className="text-sm text-[#8C8780] max-w-2xl">
              표면이익의 착시를 제거하고 4대 부대비용 및 금융비용을 정밀 반영하여 <strong className="text-[#1C1917] font-semibold">실제 순이익, 실제 ROE, AI 종합 투자 점수</strong>를 산출합니다. 워터폴 차트로 이익 잠식을 추적하고 손익분기점까지 한눈에 확인하세요.
            </p>
          </motion.div>

          {/* Main Layout */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: Input Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="lg:w-[48%]"
            >
              <SimulatorForm data={data} onChange={setData} />
            </motion.div>

            {/* Right: Result Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="lg:w-[52%]"
            >
              <div className="lg:sticky lg:top-24">
                <SimulatorResultView result={result} data={data} />
              </div>
            </motion.div>
          </div>

          {/* 하단 관련 링크 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-10 bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
          >
            <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
              <i className="ri-links-line text-[#C4956A]"></i>
              투자 전 함께 활용하면 좋은 도구
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link
                to="/bid-calculator"
                className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-lg hover:bg-[#C4956A]/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A]/20 transition-colors">
                  <i className="ri-calculator-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">입찰가 계산기</p>
                  <p className="text-xs text-[#8C8780]">적정 입찰가 3단계 산출</p>
                </div>
                <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
              </Link>
              <Link
                to="/compare"
                className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-lg hover:bg-[#C4956A]/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A]/20 transition-colors">
                  <i className="ri-scales-3-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">물건 비교 계산기</p>
                  <p className="text-xs text-[#8C8780]">다중 물건 나란히 비교</p>
                </div>
                <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
              </Link>
              <Link
                to="/auctions"
                className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-lg hover:bg-[#C4956A]/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A]/20 transition-colors">
                  <i className="ri-auction-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">경매 물건 찾기</p>
                  <p className="text-xs text-[#8C8780]">투자 가치 높은 물건 탐색</p>
                </div>
                <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
              </Link>
            </div>
          </motion.div>

        </div>
      </main>
      <Footer />
    </div>
  );
}